import { TRAINING } from './config';
import { isEffectiveSet } from './oneRm';
import { joinEs, num, plural } from './text';
import type { Advice, Exercise, MovementPattern, Muscle, WorkoutSet } from './types';

export type MuscleVolume = Partial<Record<Muscle, number>>;

/** Nombres en español para los mensajes. */
export const MUSCLE_LABELS: Record<Muscle, string> = {
  pectoral: 'pectoral',
  dorsal: 'dorsal',
  trapecio: 'trapecio',
  deltoides_anterior: 'deltoides anterior',
  deltoides_lateral: 'deltoides lateral',
  deltoides_posterior: 'deltoides posterior',
  biceps: 'bíceps',
  triceps: 'tríceps',
  antebrazo: 'antebrazo',
  cuadriceps: 'cuádriceps',
  isquiosurales: 'isquiosurales',
  gluteo: 'glúteo',
  gemelo: 'gemelo',
  aductores: 'aductores',
  abdominales: 'abdominales',
  lumbares: 'lumbares',
};

const PUSH: MovementPattern[] = ['empuje_horizontal', 'empuje_inclinado', 'empuje_vertical'];
const PULL: MovementPattern[] = ['traccion_horizontal', 'traccion_vertical'];

/**
 * Series efectivas por músculo en una semana (SPEC 7.4).
 *
 * Una serie vale 1 para el músculo principal y 0,5 para el secundario: el
 * tríceps trabaja en un press de banca, pero no como en una extensión.
 */
export function weeklySetsByMuscle(
  sets: readonly WorkoutSet[],
  exercises: ReadonlyMap<string, Exercise>,
): MuscleVolume {
  const out: MuscleVolume = {};

  for (const set of sets) {
    if (!isEffectiveSet(set)) continue;
    const exercise = exercises.get(set.exerciseId);
    if (!exercise) continue;

    for (const muscle of exercise.primary) {
      out[muscle] = (out[muscle] ?? 0) + TRAINING.primaryWeight;
    }
    for (const muscle of exercise.secondary) {
      out[muscle] = (out[muscle] ?? 0) + TRAINING.secondaryWeight;
    }
  }

  return out;
}

/** Series efectivas de empuje y de tracción en la semana. */
export function pushPullSets(
  sets: readonly WorkoutSet[],
  exercises: ReadonlyMap<string, Exercise>,
): { pushSets: number; pullSets: number } {
  let pushSets = 0;
  let pullSets = 0;

  for (const set of sets) {
    if (!isEffectiveSet(set)) continue;
    const exercise = exercises.get(set.exerciseId);
    if (!exercise) continue;
    if (PUSH.includes(exercise.pattern)) pushSets++;
    if (PULL.includes(exercise.pattern)) pullSets++;
  }

  return { pushSets, pullSets };
}

/**
 * Descompensaciones (SPEC 7.4).
 *
 * Dos reglas:
 *   1. Un músculo que SE ENTRENA pero con menos de 6 series efectivas.
 *   2. Proporción empuje/tracción fuera del rango 0,67-1,5.
 *
 * Solo se avisa de músculos que la persona SÍ entrena: recordarle cada semana
 * a alguien que no hace gemelos que no hace gemelos es ruido, no consejo. El
 * desequilibrio de grupos enteros ya lo caza la proporción empuje/tracción.
 */
export function balanceAdvice(
  weekly: MuscleVolume,
  catalog: readonly Exercise[],
  alreadyDoing: readonly string[],
  patterns?: { pushSets: number; pullSets: number },
): Advice[] {
  const out: Advice[] = [];

  // Se recorren los músculos que se ENTRENAN como principales, no los que han
  // sumado volumen. Cero series efectivas también es menos de seis: quien hace
  // gemelo cada semana solo en calentamiento no lo está entrenando, y ese es
  // justo el caso que hay que contarle. El 0,5 de un secundario no cuenta como
  // intención de entrenar ese músculo.
  for (const muscle of trainedAsPrimary(catalog, alreadyDoing)) {
    const volume = weekly[muscle] ?? 0;
    if (volume >= TRAINING.minWeeklySetsPerMuscle) continue;

    const suggestions = suggestFor(muscle, catalog, alreadyDoing);

    out.push({
      type: 'descompensacion',
      priority: 3,
      // El aviso es el déficit, no la lista: si el catálogo no da para más
      // propuestas, se dice el déficit igual en vez de callarse.
      message:
        suggestions.length > 0
          ? `Estás entrenando poco el ${MUSCLE_LABELS[muscle]}. Prueba a añadir ${joinEs(
              suggestions.map((e) => e.name),
            )}.`
          : `Estás entrenando poco el ${MUSCLE_LABELS[muscle]}. Súbele series.`,
      reason: `${plural(volume, 'serie efectiva', 'series efectivas')} esta semana, por debajo de las ${TRAINING.minWeeklySetsPerMuscle} que marca la regla.`,
    });
  }

  if (patterns && patterns.pushSets + patterns.pullSets > 0) {
    const ratioAdvice = pushPullAdvice(patterns, catalog, alreadyDoing);
    if (ratioAdvice) out.push(ratioAdvice);
  }

  return out;
}

/** Músculos que son PRINCIPALES en algo de lo que la persona está haciendo. */
function trainedAsPrimary(catalog: readonly Exercise[], alreadyDoing: readonly string[]): Muscle[] {
  const out = new Set<Muscle>();
  for (const id of alreadyDoing) {
    for (const muscle of catalog.find((e) => e.id === id)?.primary ?? []) out.add(muscle);
  }
  return [...out];
}

function suggestFor(
  muscle: Muscle,
  catalog: readonly Exercise[],
  alreadyDoing: readonly string[],
): Exercise[] {
  return catalog
    .filter((e) => e.primary.includes(muscle) && !alreadyDoing.includes(e.id))
    .slice(0, 3);
}

function pushPullAdvice(
  { pushSets, pullSets }: { pushSets: number; pullSets: number },
  catalog: readonly Exercise[],
  alreadyDoing: readonly string[],
): Advice | null {
  // Sin tracción, la proporción es infinita: el desequilibrio es máximo.
  const ratio = pullSets === 0 ? Number.POSITIVE_INFINITY : pushSets / pullSets;

  if (ratio > TRAINING.pushPullRatio.max) {
    const suggestions = byPatterns(PULL, catalog, alreadyDoing);
    return {
      type: 'descompensacion',
      priority: 2,
      message: `Empujas bastante más de lo que tiras. Añade trabajo de tracción: ${joinEs(
        suggestions.map((e) => e.name),
      )}.`,
      reason: `${num(pushSets)} series de empuje frente a ${num(pullSets)} de tracción esta semana. La regla pide quedarse entre 0,67 y 1,5.`,
    };
  }

  if (ratio < TRAINING.pushPullRatio.min) {
    const suggestions = byPatterns(PUSH, catalog, alreadyDoing);
    return {
      type: 'descompensacion',
      priority: 2,
      message: `Tiras bastante más de lo que empujas. Añade trabajo de empuje: ${joinEs(
        suggestions.map((e) => e.name),
      )}.`,
      reason: `${num(pushSets)} series de empuje frente a ${num(pullSets)} de tracción esta semana. La regla pide quedarse entre 0,67 y 1,5.`,
    };
  }

  return null;
}

function byPatterns(
  patterns: readonly MovementPattern[],
  catalog: readonly Exercise[],
  alreadyDoing: readonly string[],
): Exercise[] {
  return catalog
    .filter((e) => patterns.includes(e.pattern) && !alreadyDoing.includes(e.id))
    .slice(0, 3);
}
